/* eslint-disable react-hooks/exhaustive-deps */
/**
 * B090011 在庫照会(物流業務)
 */

import { Column, Query } from "@material-table/core";
import { Box } from "@mui/material";
import {
  B000190SokoNmr,
  B000200ShohinNmrJanCd,
  B000200ShohinNmrShohinCd,
  B090011ExcelRequest,
  B090011Find,
  B090011FindRequest,
  B090011FindResponse,
  B090011GetExcel,
  B090011Soko,
  B100190Valid,
  B100190ValidRequest,
  catchFileError,
  catchRestError,
} from "clients/rest";
import {
  msgState,
  SCodeCheck,
  SCodeSelect,
  SFileButton,
  SFrame,
  SLabel,
  SNormalButton,
  SNumberField,
  STable,
  SText,
  STextField,
} from "components";
import { getClientError, getClientMessage } from "messages";
import {
  B090011Shutsuryokujun,
  getSCode,
  SCode,
  ShohinshubetsuKbn,
} from "messages/codes";
import { labels } from "messages/labels";
import { B000190, B000200, B000202, B000221, B090016 } from "pages";
import { createRef, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { download, mapIds } from "services";
import { sokoshubetsuKbn } from "services/codes";
import { focusState, useFocus } from "states";
import { B090015 } from "./B090015";

// 呼び出し時のプロパティ
type Props = Partial<{}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  shohinCd: string;
  shohinNmrShohinCd: string;
  janCd: string;
  shohinNmrJanCd: string;
  shohinNm: string;
  shohinKana: string;
  shodaibun: string;
  shochubun: string;
  shoshobun: string;
  sokoCd: string;
  sokoNmr: string;
  locationCdFrom: string;
  locationCdTo: string;
  shohinshubetsuKbn: SCode;
  shuturyokuKbn: SCode;
  gokeisuHyoji: string;
  nyukayoteisuHyoji: string;
  zaikosuGokei: string;
  nyukayoteisuGokei: string;
  shukayoteisuGokei: string;
  // 倉庫種別指定用パラメータ
  sokoshubetsuList: string[];
}>;

// テーブルデータ定義
type Data = Partial<{
  gyo: number;
  shohinCd: string;
  shohinNmr: string;
  sokoCd: string;
  sokoNmr: string;
  zaikosu: number;
  nyukayoteisu: number;
  shukayoteisu: number;
  janCd: string;
  locationCdView: string;
  saishushireYmd: string;
  sokoshubetsuKbn: number;
  zaikosuGokei: number;
  nyukayoteisuGokei: number;
  shukayoteisuGokei: number;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  shohinCd: "",
  shohinNmrShohinCd: "",
  janCd: "",
  shohinNmrJanCd: "",
  shohinNm: "",
  shohinKana: "",
  shodaibun: "",
  shochubun: "",
  shoshobun: "",
  sokoCd: "",
  sokoNmr: "",
  locationCdFrom: "",
  locationCdTo: "",
  shohinshubetsuKbn: null,
  shuturyokuKbn: getSCode(B090011Shutsuryokujun, "0"),
  gokeisuHyoji: "0",
  nyukayoteisuHyoji: "0",
  zaikosuGokei: "",
  nyukayoteisuGokei: "",
  shukayoteisuGokei: "",
  sokoshubetsuList: ["0"],
};

// テーブルを定義する。
const columns: Column<Data>[] = [
  { field: "gyo", title: "行　", width: "4rem", align: "right" },
  { field: "shohinCd", title: "商品コード", width: "6rem" },
  { field: "shohinNmr", title: "商品名", width: "13rem" },
  { field: "sokoCd", title: "倉庫ｺｰﾄﾞ", width: "3rem" },
  { field: "sokoNmr", title: "倉庫名", width: "6rem" },
  {
    field: "zaikosu",
    title: "在庫数　",
    width: "4rem",
    align: "right",
    render: (data) => data.zaikosu?.toLocaleString(),
  },
  {
    field: "nyukayoteisu",
    title: "入荷予定数　",
    width: "4rem",
    align: "right",
    render: (data) => data.nyukayoteisu?.toLocaleString(),
  },
  {
    field: "shukayoteisu",
    title: "出荷予定数　",
    width: "4rem",
    align: "right",
    render: (data) => data.shukayoteisu?.toLocaleString(),
  },
  { field: "janCd", title: "JANコード", width: "8rem" },
  { field: "locationCdView", title: "ロケーション", width: "8rem" },
  {
    field: "saishushireYmd",
    title: "最終入荷日",
    width: "6rem",
  },
  { field: "sokoshubetsuKbn", hidden: true },
  { field: "zaikosuGokei", hidden: true },
  { field: "nyukayoteisuGokei", hidden: true },
  { field: "shukayoteisuGokei", hidden: true },
];

const focusId = "B090011";

// B090011 在庫照会(物流業務)
export const B090011 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};

const Page = (props: Props) => {
  // 状態管理の初期設定
  // テーブルの値取得用Ref
  const tableRef = createRef();
  // 初回検索用のフラグ
  const [searched, setSearched] = useState(false);
  // 検索ボタン押下時に初期ページにするステート
  const isDefaultPage = useRef(false);
  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });
  // フォーカス設定
  const { focus, register } = useRecoilValue(focusState(focusId));
  // エラー表示
  const setMsg = useSetRecoilState(msgState);
  // 各ボタン活性制御
  // 受払ボタン
  const [disabledUkeharaiButton, setDisabledUkeharaiButton] = useState(true);
  // 入荷予定ボタン
  const [disabledNyukayoteiButton, setDisabledNyukayoteiButton] =
    useState(true);
  // Excel出力ボタン
  const [disabledExcelButton, setDisabledExcelButton] = useState(true);
  // テーブル行で選択されたデータ
  const [rowData, setRowData] = useState<Partial<Data>>();
  // 画面遷移
  const navigate = useNavigate();

  // 初期表示イベント
  useEffect(() => {
    B090011Soko(methods, navigate, setMsg);
    // 初回フォーカス先
    focus(1);
  }, []);

  // ロケーションコードチェック
  // ロケーションコード(開始)チェック
  const locationCdFromValid = () => {
    const fields = methods.getValues();

    if (!fields.sokoCd || !fields.locationCdFrom) return;

    const param: B100190ValidRequest = {
      sokoCd: fields.sokoCd!,
      locationCd: fields.locationCdFrom!,
    };

    B100190Valid(param)
      .then((value) => {})
      .catch((reason) => {
        catchRestError(reason, navigate, setMsg);
      });
  };

  // ロケーションコード(終了)チェック
  const locationCdToValid = () => {
    const fields = methods.getValues();

    if (!fields.sokoCd || !fields.locationCdTo) return;

    const param: B100190ValidRequest = {
      sokoCd: fields.sokoCd!,
      locationCd: fields.locationCdTo!,
    };

    B100190Valid(param)
      .then((value) => {})
      .catch((reason) => {
        catchRestError(reason, navigate, setMsg);
      });
  };

  // 検索時の初期化処理
  const handleResetSearch = () => {
    // 各ボタン非活性化
    setDisabledUkeharaiButton(true);
    setDisabledNyukayoteiButton(true);
    // 選択されているデータを空にする
    setRowData(undefined);
    (tableRef.current as any).handleClearSelect();
    // 選択されているデータを空にする
    setRowData(undefined);
    (tableRef.current as any).handleClearSelect();
  };

  // 検索ボタンクリックイベント
  const handleClickSearchButton = () => {
    // 初期化
    handleResetSearch();
    // excelボタン非活性化
    setDisabledExcelButton(true);
    // 検索可能に変更
    setSearched(true);
    // 初期ページ設定
    isDefaultPage.current = true;
    // テーブル再検索
    (tableRef.current as any).onQueryChange();
  };

  // 検索イベント
  const handleSearch = (query: Query<Data>) => {
    return new Promise<any>((resolve) => {
      // 初回検索をしない
      if (!searched) {
        resolve({
          data: [],
          page: 0,
          totalCount: 0,
        });
        return;
      }
      // 検索フォームのデータを取得する。
      const fields = methods.getValues();

      // 出力順の値に応じたソート処理
      let order;
      if (fields.shuturyokuKbn?.value === "0") {
        order = ["shohinCd,asc", "sokoCd,asc", "locationCd,asc"];
      } else if (fields.shuturyokuKbn?.value === "1") {
        order = ["sokoCd,asc", "shohinCd,asc", "locationCd,asc"];
      } else if (fields.shuturyokuKbn?.value === "2") {
        order = [
          "shohinKana,asc",
          "shohinCd,asc",
          "sokoCd,asc",
          "locationCd,asc",
        ];
      }

      // 初期ページ表示の場合、0を設定する。
      query.page = isDefaultPage.current ? 0 : query.page;

      // 検索パラメータを作成する。
      const param: B090011FindRequest = {
        shohinCd: fields.shohinCd,
        janCd: fields.janCd,
        shohinNm: fields.shohinNm,
        shohinKana: fields.shohinKana,
        shodaibun: fields.shodaibun,
        shochubun: fields.shochubun,
        shoshobun: fields.shoshobun,
        sokoCd: fields.sokoCd!,
        locationCdFrom: fields.locationCdFrom,
        locationCdTo: fields.locationCdTo,
        shohinshubetsuKbn: fields.shohinshubetsuKbn?.value!,
        gokeisuHyoji: fields.gokeisuHyoji,
        nyukayoteisuHyoji: fields.nyukayoteisuHyoji,
        // ページネーションの設定
        page: query.page,
        size: query.pageSize,
        // ソートの設定
        sort: order,
      };
      // API呼出し
      B090011Find(param)
        // 正常取得時
        .then((value) => {
          // 合計値の設定
          handleGokei(value.data.content[0], value.data.totalElements);

          const offset = query.pageSize * query.page;
          resolve({
            data: mapIds(value.data.content, "gyo", offset),
            page: value.data.number,
            totalCount: value.data.totalElements,
          });
        })
        // エラー時
        .catch((reason) => {
          // エラー時は合計値をクリアする。
          methods.setValue("zaikosuGokei", "");
          methods.setValue("nyukayoteisuGokei", "");
          methods.setValue("shukayoteisuGokei", "");
          // 各ボタン非活性化
          setDisabledUkeharaiButton(true);
          setDisabledNyukayoteiButton(true);
          setDisabledExcelButton(true);
          catchRestError(reason, navigate, setMsg);
          resolve({
            data: [],
            page: 0,
            totalCount: 0,
          });
        })
        .finally(() => {
          // 初期ページ表示をオフにする。
          isDefaultPage.current = false;
        });
    });
  };

  // 合計値の設定
  const handleGokei = (data: B090011FindResponse, totalElements: number) => {
    // 検索結果が存在するかチェック
    if (totalElements === 0) {
      // データ非存在メッセージ表示
      setMsg(getClientError("B090011_001"));
      // 結果なしの場合、クリア
      methods.setValue("zaikosuGokei", "");
      methods.setValue("shukayoteisuGokei", "");
      methods.setValue("nyukayoteisuGokei", "");
      // 各ボタン非活性化
      setDisabledUkeharaiButton(true);
      setDisabledNyukayoteiButton(true);
      setDisabledExcelButton(true);
    }
    // 結果が1件以上取得できた場合
    else {
      // 合計値の設定
      methods.setValue("zaikosuGokei", data.zaikosuGokei ?? "");
      methods.setValue("nyukayoteisuGokei", data.nyukayoteisuGokei ?? "");
      methods.setValue("shukayoteisuGokei", data.shukayoteisuGokei ?? "");

      // Excelボタン活性化
      setDisabledExcelButton(false);
    }
  };

  // テーブル行クリックイベント
  const handleSelectRow = (_: any, rowData: Data | undefined) => {
    setRowData(rowData);
    handleUkeharaiDisabled(rowData);
    handleNyukayoteiDisabled(rowData);
  };

  // 受払照会ボタン活性制御
  const handleUkeharaiDisabled = (rowData: Data | undefined) => {
    // 選択行の倉庫種別チェックを行う。(0 の場合活性)
    if (rowData?.sokoshubetsuKbn === sokoshubetsuKbn.physicalSoko) {
      // 存在する場合活性
      setDisabledUkeharaiButton(false);
    } else {
      setDisabledUkeharaiButton(true);
    }
  };

  // 入荷予定ボタン活性制御
  const handleNyukayoteiDisabled = (rowData: Data | undefined) => {
    // 選択行の倉庫種別チェックを行う。(0 なら活性)
    if (rowData?.sokoshubetsuKbn === sokoshubetsuKbn.physicalSoko) {
      setDisabledNyukayoteiButton(false);
    } else {
      setDisabledNyukayoteiButton(true);
    }
  };

  // EXCEL出力ボタン
  const handleClickExcelButton = () => {
    // EXCEL出力フォームのデータを取得する。
    const fields = methods.getValues();

    // 出力順の値に応じたソート処理
    let order;
    if (fields.shuturyokuKbn?.value === "0") {
      order = ["shohinCd,asc", "sokoCd,asc", "locationCd,asc"];
    } else if (fields.shuturyokuKbn?.value === "1") {
      order = ["sokoCd,asc", "shohinCd,asc", "locationCd,asc"];
    } else if (fields.shuturyokuKbn?.value === "2") {
      order = [
        "shohinKana,asc",
        "shohinCd,asc",
        "sokoCd,asc",
        "locationCd,asc",
      ];
    }

    // EXCEL出力パラメータを作成する。
    const param: B090011ExcelRequest = {
      shohinCd: fields.shohinCd,
      janCd: fields.janCd,
      shohinNm: fields.shohinNm,
      shohinKana: fields.shohinKana,
      shodaibun: fields.shodaibun,
      shochubun: fields.shochubun,
      shoshobun: fields.shoshobun,
      sokoCd: fields.sokoCd!,
      locationCdFrom: fields.locationCdFrom,
      locationCdTo: fields.locationCdTo,
      shohinshubetsuKbn: fields.shohinshubetsuKbn?.value!,
      gokeisuHyoji: fields.gokeisuHyoji,
      nyukayoteisuHyoji: fields.nyukayoteisuHyoji,
      // ソートの設定
      sort: order,
    };
    B090011GetExcel(param)
      .then((response) => {
        download(response);
        setMsg(getClientMessage("B090011_001"));
      })
      .catch((reason) => {
        catchFileError(reason, navigate, setMsg);
      });
  };

  // レイアウト設定
  return (
    <FormProvider {...methods}>
      <SFrame
        title="在庫照会"
        gridTemplateColumns="6rem 3rem 3rem 5rem 15rem 10rem 7rem 5rem 5rem 2rem 1rem 4rem 6rem 5rem"
      >
        <SLabel>商品</SLabel>
        <SNumberField
          label="商品コード"
          name="shohinCd"
          gridColumn="span 3"
          code
          size={10}
          dialog={<B000200 />}
          handleSubmit={(data: any) => {
            methods.setValue("shohinCd", data.shohinCd);
            methods.setValue("shohinNmrShohinCd", data.shohinNmr);
          }}
          onBlur={() => B000200ShohinNmrShohinCd(methods, navigate, setMsg)}
          {...register(1)}
        />
        <STextField
          label="商品名"
          name="shohinNmrShohinCd"
          size={24}
          disabled
          gridColumn="span 2"
        />

        <SLabel required>倉庫</SLabel>
        <SNumberField
          label="倉庫コード"
          name="sokoCd"
          code
          required
          size={3}
          dialog={
            <B000190 sokoshubetsuList={methods.watch("sokoshubetsuList")} />
          }
          handleSubmit={(data: any) => {
            methods.setValue("sokoCd", data.sokoCd);
            methods.setValue("sokoNmr", data.sokoNmr);
          }}
          onBlur={() => B000190SokoNmr(methods, navigate, setMsg)}
          {...register(8)}
        />
        <STextField
          label="倉庫名"
          name="sokoNmr"
          size={24}
          gridColumn="span 5"
          disabled
        />

        <Box />

        <SLabel>JANコード</SLabel>
        <SNumberField
          label="JAN コード"
          name="janCd"
          gridColumn="span 3"
          code
          size={13}
          dialog={<B000200 />}
          handleSubmit={(data: any) => {
            methods.setValue("janCd", data.janCd);
            methods.setValue("shohinNmrJanCd", data.shohinNmr);
          }}
          onBlur={() => B000200ShohinNmrJanCd(methods, navigate, setMsg)}
          {...register(2)}
        />
        <STextField
          label="JAN 商品名"
          name="shohinNmrJanCd"
          size={24}
          disabled
          gridColumn="span 2"
        />

        <SLabel>ロケーション</SLabel>
        <STextField
          label="ロケーション(開始)"
          name="locationCdFrom"
          gridColumn="span 2"
          size={14}
          location
          onBlur={locationCdFromValid}
          dialog={<B000221 />}
          handleSubmit={(data: any) => {
            methods.setValue("locationCdFrom", data.locationCd);
          }}
          {...register(9)}
        />
        <SText>～</SText>
        <STextField
          label="ロケーション(終了)"
          name="locationCdTo"
          gridColumn="span 3"
          size={14}
          location
          onBlur={locationCdToValid}
          dialog={<B000221 />}
          handleSubmit={(data: any) => {
            methods.setValue("locationCdTo", data.locationCd);
          }}
          {...register(10)}
        />

        <Box />

        <SLabel>商品名</SLabel>
        <STextField
          label="商品名"
          name="shohinNm"
          size={40}
          gridColumn="span 4"
          {...register(3)}
        />

        <Box />

        <SLabel>商品種別</SLabel>
        <SCodeSelect
          label="商品種別"
          name="shohinshubetsuKbn"
          options={ShohinshubetsuKbn}
          empty
          gridColumn="span 3"
          {...register(11)}
        />

        <Box gridColumn="span 4" />

        <SLabel>カナ名</SLabel>
        <STextField
          label="カナ名"
          name="shohinKana"
          size={40}
          gridColumn="span 4"
          {...register(4)}
        />

        <Box />

        <SLabel>出力順</SLabel>
        <SCodeSelect
          label="出力順"
          name="shuturyokuKbn"
          options={B090011Shutsuryokujun}
          gridColumn="span 3"
          {...register(12)}
        />

        <Box gridColumn="span 4" />

        <SLabel>商品分類</SLabel>
        <SNumberField
          label="商品大分類"
          name="shodaibun"
          code
          size={3}
          {...register(5)}
        />
        <SNumberField
          label="商品中分類"
          name="shochubun"
          code
          size={3}
          {...register(6)}
        />
        <SNumberField
          label="商品小分類"
          name="shoshobun"
          code
          size={3}
          dialog={<B000202 />}
          handleSubmit={(data: any) => {
            methods.setValue("shodaibun", data.shodaibun);
            methods.setValue("shochubun", data.shochubun);
            methods.setValue("shoshobun", data.shoshobun);
          }}
          {...register(7)}
        />

        <Box gridColumn="span 5" />

        <Box gridColumn="span 6" />

        <SLabel>入荷予定数表示</SLabel>
        <SCodeCheck
          label="入荷予定数表示"
          name="nyukayoteisuHyoji"
          on={1}
          off={0}
          {...register(13)}
        />

        <SLabel gridColumn="span 2">合計数表示</SLabel>
        <SCodeCheck
          label="合計数表示"
          name="gokeisuHyoji"
          on={1}
          off={0}
          {...register(14)}
        />

        <Box gridColumn="span 2" />

        <SNormalButton
          label={labels.buttons.search}
          onClick={handleClickSearchButton}
          align="right"
          {...register(15)}
        />
      </SFrame>

      <SFrame gridTemplateColumns="53.5rem 8rem 8rem 8rem">
        <Box />
        <SLabel>在庫数合計</SLabel>
        <SLabel>入荷予定数合計</SLabel>
        <SLabel>出荷予定数合計</SLabel>

        <Box />
        <SNumberField
          label="在庫数合計"
          name="zaikosuGokei"
          align="right"
          disabled
        />
        <SNumberField
          label="入荷予定数合計"
          name="nyukayoteisuGokei"
          align="right"
          disabled
        />
        <SNumberField
          label="出荷予定数合計"
          name="shukayoteisuGokei"
          align="right"
          disabled
        />
      </SFrame>
      <SFrame>
        <STable
          keyName="gyo"
          data={handleSearch}
          columns={columns}
          tableRef={tableRef}
          onRowClick={handleSelectRow}
          width={1250}
          height={300}
          onPageChange={handleResetSearch}
        />
      </SFrame>
      <SFrame gridTemplateColumns="58rem 6rem 7rem 6.5rem">
        <Box />

        <SNormalButton
          label={labels.buttons.ukeharai}
          disabled={disabledUkeharaiButton}
          align="right"
          {...register(16)}
          dialog={
            <B090015
              sokoCd={rowData?.sokoCd}
              sokoNmr={rowData?.sokoNmr}
              shohinCd={rowData?.shohinCd}
              shohinNmr={rowData?.shohinNmr}
              locationCd={rowData?.locationCdView}
            />
          }
          dialogAction="close"
        />
        <SNormalButton
          label={labels.buttons.nyukayotei}
          disabled={disabledNyukayoteiButton}
          align="right"
          {...register(17)}
          dialog={
            <B090016
              sokoCd={rowData?.sokoCd}
              sokoNmr={rowData?.sokoNmr}
              shohinCd={rowData?.shohinCd}
              shohinNmr={rowData?.shohinNmr}
              locationCd={rowData?.locationCdView}
            />
          }
          dialogAction="close"
        />
        <SFileButton
          label={labels.buttons.excel}
          onClick={handleClickExcelButton}
          disabled={disabledExcelButton}
          align="right"
          {...register(18)}
        />
      </SFrame>
    </FormProvider>
  );
};
